import { useState, useEffect, type RefObject } from 'react';

/**
 * Hook for tracking which page section is currently being viewed.
 *
 * The active section is the last one whose top edge has scrolled past the
 * bottom of the fixed navbar. That single rule makes scrolling down and
 * scrolling up settle on identical boundaries.
 *
 * An IntersectionObserver whose root is collapsed to a ~1px line just below
 * the navbar does the work: a section crossing that line is exactly the moment
 * the active item changes, so the callback runs only at real boundaries rather
 * than on every scroll frame.
 *
 * @param ids - Section element ids, in the order they appear on the page
 * @param offsetRef - Ref to the fixed navbar; its height becomes the detection line
 */
export function useActiveSection(
  ids: string[],
  offsetRef: RefObject<HTMLElement>
): string {
  const [activeId, setActiveId] = useState(ids[0] ?? '');

  // Depend on the joined key so a stable array literal isn't required at the call site
  const idsKey = ids.join(',');

  useEffect(() => {
    const sectionIds = idsKey.split(',').filter(Boolean);
    if (sectionIds.length === 0) return;

    let observer: IntersectionObserver | null = null;

    const connect = () => {
      observer?.disconnect();

      const elements = sectionIds
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => el !== null);

      if (elements.length === 0) return;

      // Detection line sits just under the navbar, with a small buffer so a
      // section counts as current slightly before it touches the navbar itself.
      const navHeight = offsetRef.current?.getBoundingClientRect().bottom ?? 0;
      const offset = navHeight + 16;

      // Collapse the viewport to a 1px band at that line
      const bottomMargin = Math.max(0, window.innerHeight - offset - 1);

      const update = () => {
        let nextId = sectionIds[0];

        for (const el of elements) {
          if (el.getBoundingClientRect().top <= offset) {
            nextId = el.id;
          }
        }

        setActiveId((prev) => (prev === nextId ? prev : nextId));
      };

      observer = new IntersectionObserver(update, {
        rootMargin: `-${offset}px 0px -${bottomMargin}px 0px`,
        threshold: 0,
      });

      elements.forEach((el) => observer?.observe(el));
    };

    connect();

    // rootMargin is in pixels, so the band has to be rebuilt when the viewport
    // or the navbar height changes. Rebuilding sets no state, so no re-render.
    window.addEventListener('resize', connect);

    return () => {
      window.removeEventListener('resize', connect);
      observer?.disconnect();
    };
  }, [idsKey, offsetRef]);

  return activeId;
}
